/**
 * ChartCycleService — tracks 28-day AIRAC cycles and warns when cached
 * approach plates are close to expiring.
 *
 * AIRAC cycle reference: 2020-01-02 was cycle 2001 (effective date).
 * Every 28 days from that anchor is a new cycle. The identifier format is
 * YYCC where YY is the two-digit year of the effective date and CC is the
 * 1-indexed cycle number within the year.
 *
 * This implementation keeps the math simple and local so it works offline.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CYCLE_DAYS = 28;

// Anchor: AIRAC cycle 2001 (2020-01-02) — verified against FAA charts calendar.
const ANCHOR_DATE = new Date(Date.UTC(2020, 0, 2));
const ANCHOR_CYCLE_NUMBER = 1;  // "01" portion of 2001
const ANCHOR_CYCLE_YEAR   = 20; // "20" portion of 2001

export interface CycleInfo {
  /** 4-char cycle ID, e.g. "2604". */
  id: string;
  /** Effective date (UTC midnight). */
  effectiveDate: Date;
  /** Expiration date (inclusive, UTC midnight; == next effective - 1ms). */
  expirationDate: Date;
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / MS_PER_DAY);
}

export function getCycleInfoForDate(date: Date = new Date()): CycleInfo {
  const days = daysBetween(ANCHOR_DATE, date);
  const cyclesSinceAnchor = Math.floor(days / CYCLE_DAYS);
  const effective = new Date(ANCHOR_DATE.getTime() + cyclesSinceAnchor * CYCLE_DAYS * MS_PER_DAY);
  const nextEffective = new Date(effective.getTime() + CYCLE_DAYS * MS_PER_DAY);

  // Derive YYCC id. "Cycle number" resets each calendar year based on
  // effective-date year. We walk back to find the first cycle whose
  // effectiveDate year matches the current cycle's effectiveDate year.
  const year = effective.getUTCFullYear();
  let probe = new Date(effective.getTime());
  let cycleInYear = 1;
  while (probe.getTime() > ANCHOR_DATE.getTime()) {
    const prev = new Date(probe.getTime() - CYCLE_DAYS * MS_PER_DAY);
    if (prev.getUTCFullYear() !== year) break;
    cycleInYear++;
    probe = prev;
  }
  // If we never walked (effective itself is before anchor), fall back to anchor label
  const yy = String(year).slice(-2);
  const cc = String(cycleInYear).padStart(2, '0');
  const id = `${yy}${cc}`;

  return {
    id,
    effectiveDate: effective,
    expirationDate: new Date(nextEffective.getTime() - 1),
  };
}

export function getCurrentCycle(): CycleInfo {
  return getCycleInfoForDate(new Date());
}

export function daysUntilExpiration(cycle: CycleInfo, now: Date = new Date()): number {
  return Math.ceil((cycle.expirationDate.getTime() - now.getTime()) / MS_PER_DAY);
}

export function isExpired(cycle: CycleInfo, now: Date = new Date()): boolean {
  return now.getTime() > cycle.expirationDate.getTime();
}

export interface CycleWarning {
  level: 'ok' | 'warning' | 'expired';
  message: string;
  daysRemaining: number;
}

export function getCycleWarning(cycleId: string | CycleInfo | undefined, now: Date = new Date()): CycleWarning {
  if (!cycleId) {
    return { level: 'warning', message: 'Unknown cycle', daysRemaining: 0 };
  }
  const cycle = typeof cycleId === 'string' ? parseCycleId(cycleId) : cycleId;
  if (!cycle) {
    return { level: 'warning', message: `Malformed cycle id: ${String(cycleId)}`, daysRemaining: 0 };
  }
  const days = daysUntilExpiration(cycle, now);
  if (isExpired(cycle, now)) {
    return { level: 'expired', message: `Cycle ${cycle.id} expired ${Math.abs(days)}d ago`, daysRemaining: days };
  }
  if (days <= 7) {
    return { level: 'warning', message: `Cycle ${cycle.id} expires in ${days}d`, daysRemaining: days };
  }
  return { level: 'ok', message: `Cycle ${cycle.id} \u2014 ${days}d remaining`, daysRemaining: days };
}

/** Parse a 4-digit YYCC identifier back into a CycleInfo, best-effort. */
export function parseCycleId(id: string): CycleInfo | null {
  if (!/^\d{4}$/.test(id)) return null;
  const yy = parseInt(id.slice(0, 2), 10);
  const cc = parseInt(id.slice(2, 4), 10);
  // Assume 20YY; the d-TPP index doesn't use 19YY cycles any more.
  const targetYear = 2000 + yy;

  // Walk cycles until we find the one matching year+cycleInYear.
  let probe = new Date(ANCHOR_DATE.getTime());
  let cycleInYear = ANCHOR_CYCLE_NUMBER;
  let currentYear = ANCHOR_CYCLE_YEAR + 2000;
  while (probe.getTime() < Date.UTC(targetYear + 2, 0, 1)) {
    if (currentYear === targetYear && cycleInYear === cc) {
      const expiration = new Date(probe.getTime() + CYCLE_DAYS * MS_PER_DAY - 1);
      return { id, effectiveDate: new Date(probe.getTime()), expirationDate: expiration };
    }
    const next = new Date(probe.getTime() + CYCLE_DAYS * MS_PER_DAY);
    if (next.getUTCFullYear() !== currentYear) {
      currentYear = next.getUTCFullYear();
      cycleInYear = 1;
    } else {
      cycleInYear++;
    }
    probe = next;
  }
  return null;
}
